import classNames from "classnames";
import { CSSProperties, useRef } from "react";
import VariableSuggestionPopper from "../variables/VariableSuggestionPopper";
import { Variable } from "../../models/interactions/Variable";
import RichTextSelectionToolbar from "./RichTextSelectionToolbar";
import useRichTextEditorController from "./useRichTextEditorController.hook";
import "./RichTextEditor.css";

interface RichTextEditorProps {
  value: string;
  onCommit: (nextValue: string) => void;
  variables: Variable[];
  ariaLabel?: string;
  placeholder?: string;
  className?: string;
  contentClassName?: string;
  contentStyle?: CSSProperties;
  warningClassName?: string;
  debounceMs?: number;
  allowVariables?: boolean;
  checkVariables?: boolean;
  enableVariableIntellisense?: boolean;
  enableFormattingToolbar?: boolean;
  flushOnCleanupKeys?: readonly unknown[];
  onSubmitEnter?: (currentValue: string) => boolean | void;
  suggestionsMaxWidth?: number;
  disabled?: boolean;
  autoFocus?: boolean;
}

/**
 * WYSIWYG editor for interaction message fields. Stores a constrained HTML
 * format (see RichTextHtmlUtils.ts) in the localized string value and keeps
 * variable tokens working as non-editable chips with intellisense.
 */
export default function RichTextEditor({
  value,
  onCommit,
  variables,
  ariaLabel,
  placeholder,
  className,
  contentClassName,
  contentStyle,
  warningClassName,
  debounceMs = 500,
  allowVariables = true,
  checkVariables = true,
  enableVariableIntellisense = true,
  enableFormattingToolbar = true,
  flushOnCleanupKeys = [],
  onSubmitEnter,
  suggestionsMaxWidth,
  disabled = false,
  autoFocus = false,
}: RichTextEditorProps) {
  const wrapperRef = useRef<HTMLDivElement>(null);

  const {
    editorRef,
    warningText,
    persistImmediateValue,
    handleInput,
    handleFocus,
    handleBlur,
    handleClick,
    handleKeyUp,
    handleKeyDown,
    handlePaste,
    suggestions,
  } = useRichTextEditorController({
    value,
    onCommit,
    variables,
    debounceMs,
    allowVariables,
    checkVariables,
    enableVariableIntellisense,
    flushOnCleanupKeys,
    onSubmitEnter,
  });

  return (
    <div ref={wrapperRef} className={className}>
      <div
        ref={editorRef}
        role="textbox"
        aria-multiline="true"
        aria-label={ariaLabel}
        className={classNames("rich-text-editor", contentClassName)}
        style={contentStyle}
        contentEditable={!disabled}
        suppressContentEditableWarning
        data-placeholder={placeholder}
        autoFocus={autoFocus}
        onInput={handleInput}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onClick={handleClick}
        onKeyUp={handleKeyUp}
        onKeyDown={(e) => {
          // Keep typing from reaching global shortcut handlers and dnd-kit
          // keyboard sensors on ancestor elements.
          e.stopPropagation();
          handleKeyDown(e);
        }}
        onPaste={handlePaste}
        onScroll={(e) => e.stopPropagation()}
        onWheel={(e) => e.stopPropagation()}
      />
      {enableFormattingToolbar && !disabled && (
        <RichTextSelectionToolbar editorRef={editorRef} onApplied={persistImmediateValue} />
      )}
      {suggestions.open && (
        <VariableSuggestionPopper
          open={true}
          anchorEl={suggestions.anchorEl ?? wrapperRef.current}
          width={wrapperRef.current?.clientWidth}
          maxWidth={suggestionsMaxWidth}
          suggestions={suggestions.filteredSuggestions}
          selectedIndex={suggestions.selectedIndex}
          menuRef={suggestions.menuRef}
          onSelect={suggestions.onSelect}
        />
      )}
      {checkVariables && warningText && (
        <div
          className={
            warningClassName ?? "mt-[4px] text-[12px] font-medium text-creator-error"
          }
        >
          {warningText}
        </div>
      )}
    </div>
  );
}
